import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiCaretRightBold,
    PiCheckCircleFill,
    PiDeviceMobileBold,
    PiEnvelopeSimpleBold,
    PiKeyBold,
    PiShieldCheckFill,
    PiShieldSlashFill,
    PiSignInBold,
    PiTimerBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Button, PageHeader, Skeleton, Switch } from '@v2/design-system';
import api from '@v2/lib/api';
import { isSuperAdmin } from '@v2/lib/auth';
import { cn } from '@v2/utils/cn';
import UsersAdminTabs from '@v2/features/users/UsersAdminTabs';
import { useI18n } from '@v2/providers/I18nProvider';

type LoginSecurityData = {
    mfa_enabled: boolean;
    email_enabled: boolean;
    ttl_minutes: number;
    status_message: string;
    is_active: boolean;
};

type Recent = {
    logins: number;
    people: number;
    failed: number;
    failed_unknown: number;
    top_failed: { user_id: number; name: string; attempts: number; last_at: string }[];
};

type FormState = {
    mfa_enabled: boolean;
    email_enabled: boolean;
    ttl_minutes: string;
};

const TTL_PRESETS = [5, 10, 15, 30];

function SettingRow({
    icon,
    title,
    help,
    checked,
    disabled,
    onChange,
}: {
    icon: ReactNode;
    title: string;
    help: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <div className={cn('flex items-start gap-4 px-5 py-4', disabled && 'opacity-50')}>
            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {icon}
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{help}</p>
            </div>
            <Switch
                size="sm"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                aria-label={title}
                className="shrink-0 [&_label]:my-0"
            />
        </div>
    );
}

function FlowStep({ icon, label, sub, state }: { icon: ReactNode; label: string; sub?: string; state: 'on' | 'off' | 'done' }) {
    return (
        <div className="flex min-w-0 flex-1 flex-col items-center text-center">
            <span
                className={cn(
                    'flex size-10 items-center justify-center rounded-full text-lg',
                    state === 'on' && 'bg-white text-gray-800 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600',
                    state === 'done' && 'bg-emerald-500 text-white',
                    state === 'off' && 'border border-dashed border-gray-300 text-gray-300 dark:border-gray-600 dark:text-gray-600',
                )}
            >
                {icon}
            </span>
            <span className={cn('mt-1.5 text-xs font-semibold', state === 'off' ? 'text-gray-400 line-through' : 'text-gray-800 dark:text-gray-100')}>
                {label}
            </span>
            {sub && <span className="text-[11px] text-gray-500">{sub}</span>}
        </div>
    );
}

export default function LoginSecuritySettingsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [form, setForm] = useState<FormState>({ mfa_enabled: true, email_enabled: true, ttl_minutes: '10' });
    const [customTtl, setCustomTtl] = useState(false);

    const settingsQuery = useQuery({
        queryKey: ['settings', 'login-security'],
        queryFn: async () => (await api.get<{ data: LoginSecurityData; recent?: Recent }>('/settings/login-security')).data,
        enabled: isSuperAdmin(),
    });

    const saved = settingsQuery.data?.data;
    const recent = settingsQuery.data?.recent;

    useEffect(() => {
        if (saved) {
            setForm({ mfa_enabled: saved.mfa_enabled, email_enabled: saved.email_enabled, ttl_minutes: String(saved.ttl_minutes) });
            setCustomTtl(!TTL_PRESETS.includes(saved.ttl_minutes));
        }
    }, [saved]);

    const minutes = Number(form.ttl_minutes) || 10;
    const willChallenge = form.mfa_enabled && form.email_enabled;
    const ttlInvalid = form.mfa_enabled && (!/^\d+$/.test(form.ttl_minutes) || minutes < 1 || minutes > 120);

    const dirty =
        !!saved &&
        (saved.mfa_enabled !== form.mfa_enabled || saved.email_enabled !== form.email_enabled || String(saved.ttl_minutes) !== form.ttl_minutes);

    const statusPreview = useMemo(() => {
        if (!form.mfa_enabled) return t('auth.security_settings.status_off');
        if (!form.email_enabled) return t('auth.security_settings.status_mfa_without_email');
        return t('auth.security_settings.status_on', { minutes });
    }, [form, minutes, t]);

    const saveMutation = useMutation({
        mutationFn: async () =>
            api.put('/settings/login-security', {
                mfa_enabled: form.mfa_enabled,
                email_enabled: form.email_enabled,
                ttl_minutes: minutes,
            }),
        onSuccess: () => {
            toast.success(t('auth.security_settings.saved'));
            qc.invalidateQueries({ queryKey: ['settings', 'login-security'] });
            qc.invalidateQueries({ queryKey: ['meta', 'navigation'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? t('v2.common.load_product_error'));
        },
    });

    if (!isSuperAdmin()) {
        return <Navigate to="/users" replace />;
    }

    const header = (
        <>
            <PageHeader
                title={t('auth.security_settings.nav')}
                breadcrumb={[
                    { name: t('v2.common.dashboard'), href: '/' },
                    { name: t('v2.nav.group.users') },
                    { name: t('auth.security_settings.nav') },
                ]}
            />
            <UsersAdminTabs />
        </>
    );

    if (settingsQuery.isLoading) {
        return (
            <div>
                {header}
                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
                    <div className="space-y-5">
                        <Skeleton className="h-40 rounded-2xl" />
                        <Skeleton className="h-64 rounded-2xl" />
                    </div>
                    <Skeleton className="h-72 rounded-2xl" />
                </div>
            </div>
        );
    }

    if (settingsQuery.isError || !saved) {
        return (
            <div>
                {header}
                <p className="text-sm text-rose-600">{t('auth.security_settings.forbidden')}</p>
            </div>
        );
    }

    return (
        <div className={cn('@container', dirty && 'pb-24')}>
            {header}

            <div className="grid items-start gap-5 @5xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-5">
                    <section
                        className={cn(
                            'overflow-hidden rounded-2xl border',
                            willChallenge
                                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20'
                                : 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20',
                        )}
                    >
                        <div className="flex items-start gap-4 px-5 pt-5">
                            <span
                                className={cn(
                                    'flex size-12 shrink-0 items-center justify-center rounded-xl text-2xl text-white',
                                    willChallenge ? 'bg-emerald-500' : 'bg-amber-500',
                                )}
                            >
                                {willChallenge ? <PiShieldCheckFill /> : <PiShieldSlashFill />}
                            </span>
                            <div className="min-w-0">
                                <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                                    {willChallenge ? t('auth.security_settings.status_active') : t('auth.security_settings.status_inactive')}
                                    {dirty && (
                                        <span className="ml-2 rounded-full bg-white/70 px-2 py-0.5 align-middle text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600">
                                            {t('auth.security_settings.unsaved')}
                                        </span>
                                    )}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{statusPreview}</p>
                            </div>
                        </div>
                        <div className="mt-4 border-t border-black/5 bg-white/50 px-5 py-4 dark:border-white/5 dark:bg-gray-900/30">
                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('auth.security_settings.flow_title')}</p>
                            <div className="flex items-start gap-1">
                                <FlowStep icon={<PiKeyBold />} label={t('auth.security_settings.flow_password')} state="on" />
                                <PiCaretRightBold className="mt-3 shrink-0 text-gray-300" />
                                <FlowStep
                                    icon={<PiEnvelopeSimpleBold />}
                                    label={t('auth.security_settings.flow_code')}
                                    sub={
                                        willChallenge
                                            ? t('auth.security_settings.flow_code_minutes', { minutes })
                                            : t('auth.security_settings.flow_skipped')
                                    }
                                    state={willChallenge ? 'on' : 'off'}
                                />
                                <PiCaretRightBold className="mt-3 shrink-0 text-gray-300" />
                                <FlowStep icon={<PiCheckCircleFill />} label={t('auth.security_settings.flow_done')} state="done" />
                            </div>
                        </div>
                    </section>

                    <section className="divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800/60">
                        <SettingRow
                            icon={<PiShieldCheckFill />}
                            title={t('auth.security_settings.mfa_enabled')}
                            help={t('auth.security_settings.mfa_enabled_help')}
                            checked={form.mfa_enabled}
                            onChange={(v) => setForm((prev) => ({ ...prev, mfa_enabled: v, email_enabled: v ? true : prev.email_enabled }))}
                        />
                        <SettingRow
                            icon={<PiEnvelopeSimpleBold />}
                            title={t('auth.security_settings.email_enabled')}
                            help={t('auth.security_settings.email_enabled_help')}
                            checked={form.email_enabled}
                            disabled={!form.mfa_enabled}
                            onChange={(v) => setForm((prev) => ({ ...prev, email_enabled: v }))}
                        />
                        <div className={cn('flex flex-wrap items-start gap-4 px-5 py-4', !willChallenge && 'opacity-50')}>
                            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                <PiTimerBold />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('auth.security_settings.ttl_minutes')}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{t('auth.security_settings.ttl_minutes_help')}</p>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    {TTL_PRESETS.map((m) => {
                                        const on = !customTtl && minutes === m;
                                        return (
                                            <button
                                                key={m}
                                                type="button"
                                                disabled={!willChallenge}
                                                onClick={() => {
                                                    setCustomTtl(false);
                                                    setForm((prev) => ({ ...prev, ttl_minutes: String(m) }));
                                                }}
                                                aria-pressed={on}
                                                className={cn(
                                                    'rounded-lg border px-3 py-1.5 text-sm font-medium tabular-nums transition',
                                                    on
                                                        ? 'border-primary bg-primary text-white'
                                                        : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700',
                                                )}
                                            >
                                                {m} {t('auth.security_settings.ttl_suffix')}
                                            </button>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        disabled={!willChallenge}
                                        onClick={() => setCustomTtl(true)}
                                        aria-pressed={customTtl}
                                        className={cn(
                                            'rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                                            customTtl
                                                ? 'border-primary bg-primary text-white'
                                                : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700',
                                        )}
                                    >
                                        {t('auth.security_settings.ttl_custom')}
                                    </button>
                                    {customTtl && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <input
                                                type="number"
                                                min={1}
                                                max={120}
                                                disabled={!willChallenge}
                                                value={form.ttl_minutes}
                                                onChange={(e) => setForm((prev) => ({ ...prev, ttl_minutes: e.target.value }))}
                                                aria-label={t('auth.security_settings.ttl_minutes')}
                                                className={cn(
                                                    'h-9 w-20 rounded-lg border bg-white px-2 text-sm tabular-nums outline-none focus:border-primary dark:bg-gray-900',
                                                    ttlInvalid ? 'border-rose-400' : 'border-gray-200 dark:border-gray-600',
                                                )}
                                                autoFocus
                                            />
                                            <span className="text-sm text-gray-500">{t('auth.security_settings.ttl_suffix')}</span>
                                        </span>
                                    )}
                                </div>
                                {ttlInvalid && <p className="mt-1 text-xs text-rose-600">1 – 120 {t('auth.security_settings.ttl_suffix')}</p>}
                            </div>
                        </div>
                    </section>

                    {!willChallenge && (
                        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                            <PiWarningBold className="mt-0.5 size-5 shrink-0" />
                            {t('auth.security_settings.off_warning')}
                        </div>
                    )}

                    <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <PiDeviceMobileBold className="mt-0.5 size-4 shrink-0" />
                        {t('auth.security_settings.app_note')}
                    </p>
                </div>

                <aside className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60 @5xl:sticky @5xl:top-20">
                    <header className="border-b border-gray-100 px-5 py-3 dark:border-gray-700">
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('auth.security_settings.recent_title')}</h2>
                    </header>
                    {recent ? (
                        <div className="p-5">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-900/40">
                                    <PiSignInBold className="mb-1 text-lg text-sky-600" />
                                    <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{recent.logins}</p>
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('auth.security_settings.recent_logins')}</p>
                                    <p className="text-[11px] text-gray-400">{t('auth.security_settings.recent_people', { count: recent.people })}</p>
                                </div>
                                <div className={cn('rounded-xl p-3', recent.failed > 0 ? 'bg-rose-50 dark:bg-rose-950/30' : 'bg-gray-50 dark:bg-gray-900/40')}>
                                    <PiWarningBold className={cn('mb-1 text-lg', recent.failed > 0 ? 'text-rose-600' : 'text-gray-400')} />
                                    <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{recent.failed}</p>
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('auth.security_settings.recent_failed')}</p>
                                    {recent.failed_unknown > 0 && (
                                        <p className="text-[11px] text-gray-500">
                                            {t('auth.security_settings.recent_failed_unknown', { count: recent.failed_unknown })}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('auth.security_settings.recent_top')}</p>
                            {recent.top_failed.length === 0 ? (
                                <p className="text-xs text-gray-400">{t('auth.security_settings.recent_none')}</p>
                            ) : (
                                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {recent.top_failed.map((u) => (
                                        <li key={u.user_id}>
                                            <Link
                                                to={`/users/${u.user_id}/activity-log?tab=account`}
                                                className="flex items-center justify-between gap-2 py-2 text-sm hover:text-primary"
                                            >
                                                <span className="min-w-0 truncate font-medium">{u.name}</span>
                                                <span className="shrink-0 text-right text-xs text-gray-500">
                                                    <span className="font-semibold text-rose-600">
                                                        {t('auth.security_settings.recent_attempts', { count: u.attempts })}
                                                    </span>
                                                    <br />
                                                    {t('auth.security_settings.recent_last', { date: u.last_at })}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ) : (
                        <Skeleton className="m-5 h-32 rounded-xl" />
                    )}
                </aside>
            </div>

            {dirty && (
                <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 lg:left-[270px]">
                    <div className="flex flex-wrap items-center gap-3 px-6 py-3">
                        <PiWarningBold className="size-5 text-amber-500" />
                        <p className="text-sm text-gray-700 dark:text-gray-200">{t('auth.security_settings.unsaved')}</p>
                        <div className="ml-auto flex gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setForm({ mfa_enabled: saved.mfa_enabled, email_enabled: saved.email_enabled, ttl_minutes: String(saved.ttl_minutes) });
                                    setCustomTtl(!TTL_PRESETS.includes(saved.ttl_minutes));
                                }}
                                disabled={saveMutation.isPending}
                                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            >
                                {t('auth.security_settings.discard')}
                            </button>
                            <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || ttlInvalid}>
                                {saveMutation.isPending ? '…' : t('common.save')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
